/**
 * Event Validation Utilities
 * Comprehensive validation for event creation and updates
 */

// Maximum field lengths
const MAX_LENGTHS = {
  NAME: 100,
  SUBHEADER: 300,
  DESCRIPTION: 300,
  LOCATION: 500,
  TIME: 200,
  URL: 2000,
};

/**
 * Sanitizes text by removing HTML tags and trimming
 */
export const sanitizeText = (text) => {
  if (!text) return '';
  return text
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, '') // Remove control characters
    .trim();
};

/**
 * Validates text field for XSS, length, and content
 */
export const validateTextField = (value, fieldName, maxLength, isRequired = true) => {
  const errors = [];

  if (!value || value.trim() === '') {
    if (isRequired) {
      errors.push(`${fieldName} is required`);
    }
    return { isValid: !isRequired, errors };
  }

  // Check for only whitespace
  if (value.trim().length === 0) {
    errors.push(`${fieldName} cannot contain only whitespace`);
  }

  // Check length
  if (value.length > maxLength) {
    errors.push(
      `${fieldName} must be less than ${maxLength} characters (current: ${value.length})`
    );
  }

  // Check for script tags (basic XSS prevention)
  if (/<script|javascript:|onerror=/i.test(value)) {
    errors.push(`${fieldName} contains potentially malicious content`);
  }

  // Check for excessive line breaks
  const lineBreaks = (value.match(/\n/g) || []).length;
  if (lineBreaks > 50) {
    errors.push(`${fieldName} contains too many line breaks`);
  }

  return {
    isValid: errors.length === 0,
    errors,
    sanitized: sanitizeText(value),
  };
};

/**
 * Validates URL format and security
 */
export const validateURL = (url, fieldName, isRequired = false) => {
  const errors = [];

  if (!url || url.trim() === '') {
    if (isRequired) {
      errors.push(`${fieldName} is required`);
    }
    return { isValid: !isRequired, errors };
  }

  const trimmedUrl = url.trim();

  // Check length
  if (trimmedUrl.length > MAX_LENGTHS.URL) {
    errors.push(`${fieldName} is too long (max ${MAX_LENGTHS.URL} characters)`);
  }

  // Check for valid protocol
  if (!trimmedUrl.match(/^https?:\/\//i)) {
    errors.push(`${fieldName} must start with http:// or https://`);
    return { isValid: false, errors };
  }

  // Block dangerous protocols
  if (trimmedUrl.match(/^(javascript|data|file|ftp):/i)) {
    errors.push(`${fieldName} contains an invalid protocol`);
  }

  // Check for spaces
  if (trimmedUrl.includes(' ')) {
    errors.push(`${fieldName} cannot contain spaces`);
  }

  // Try to validate URL format
  try {
    const urlObj = new URL(trimmedUrl);

    // Block localhost and internal IPs (optional - comment out if needed)
    if (
      urlObj.hostname === 'localhost' ||
      urlObj.hostname === '127.0.0.1' ||
      urlObj.hostname.match(/^192\.168\.|^10\.|^172\.(1[6-9]|2[0-9]|3[0-1])\./)
    ) {
      errors.push(`${fieldName} cannot point to localhost or internal IP addresses`);
    }
  } catch (e) {
    errors.push(`${fieldName} is not a valid URL format`);
  }

  return {
    isValid: errors.length === 0,
    errors,
    sanitized: trimmedUrl,
  };
};

/**
 * Validates image URL specifically
 */
export const validateImageURL = (url) => {
  const errors = [];

  if (!url || url.trim() === '') {
    errors.push('Image URL is required');
    return { isValid: false, errors };
  }

  // First validate as regular URL
  const urlValidation = validateURL(url, 'Image URL', true);
  if (!urlValidation.isValid) {
    return urlValidation;
  }

  const trimmedUrl = url.trim();

  // Check if it looks like an image (optional - may have false negatives)
  const hasImageExtension = /\.(jpg|jpeg|png|gif|webp|svg|bmp)(\?.*)?$/i.test(trimmedUrl);
  const isDataUri = trimmedUrl.startsWith('data:image/');

  if (
    !hasImageExtension &&
    !isDataUri &&
    !trimmedUrl.includes('/img') &&
    !trimmedUrl.includes('/image')
  ) {
    // Soft warning - don't block but warn user
    errors.push('Warning: URL may not point to an image file. Verify the URL displays an image.');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings: errors, // Store as warnings instead for image validation
    sanitized: trimmedUrl,
  };
};

/**
 * Validates date fields
 */
export const validateDate = (dateString, fieldName, isRequired = true) => {
  const errors = [];

  if (!dateString || dateString.trim() === '') {
    if (isRequired) {
      errors.push(`${fieldName} is required`);
    }
    return { isValid: !isRequired, errors };
  }

  // Try to parse date
  const date = new Date(dateString);

  if (isNaN(date.getTime())) {
    errors.push(`${fieldName} is not a valid date`);
    return { isValid: false, errors };
  }

  // Check if date is too far in the past (before 1900)
  if (date.getFullYear() < 1900) {
    errors.push(`${fieldName} cannot be before year 1900`);
  }

  // Check if date is too far in the future (more than 50 years)
  const fiftyYearsFromNow = new Date();
  fiftyYearsFromNow.setFullYear(fiftyYearsFromNow.getFullYear() + 50);

  if (date > fiftyYearsFromNow) {
    errors.push(`${fieldName} cannot be more than 50 years in the future`);
  }

  return {
    isValid: errors.length === 0,
    errors,
    date,
  };
};

/**
 * Validates start and end date relationship
 */
export const validateDateRange = (startDateString, endDateString) => {
  const errors = [];

  if (!startDateString) {
    errors.push('Start date is required');
    return { isValid: false, errors };
  }

  const startDate = new Date(startDateString);

  if (isNaN(startDate.getTime())) {
    errors.push('Start date is not valid');
    return { isValid: false, errors };
  }

  // If no end date, that's okay (single day event)
  if (!endDateString || endDateString.trim() === '') {
    return { isValid: true, errors: [] };
  }

  const endDate = new Date(endDateString);

  if (isNaN(endDate.getTime())) {
    errors.push('End date is not valid');
    return { isValid: false, errors };
  }

  // Check if end date is before start date
  if (endDate < startDate) {
    errors.push('End date must be on or after start date');
  }

  // Check if date range is excessively long (e.g., more than 1 year)
  const daysDiff = (endDate - startDate) / (1000 * 60 * 60 * 24);
  if (daysDiff > 365) {
    errors.push('Event duration cannot exceed 1 year');
  }

  return {
    isValid: errors.length === 0,
    errors,
    startDate,
    endDate,
    daysDiff,
  };
};

/**
 * Validates time field format
 */
export const validateTime = (timeString) => {
  const errors = [];

  if (!timeString || timeString.trim() === '') {
    errors.push('Time is required');
    return { isValid: false, errors };
  }

  const trimmed = timeString.trim();

  // Check length
  if (trimmed.length > MAX_LENGTHS.TIME) {
    errors.push(`Time must be less than ${MAX_LENGTHS.TIME} characters`);
  }

  // Check for script injection
  if (/<script|javascript:/i.test(trimmed)) {
    errors.push('Time contains invalid characters');
  }

  // Basic format check for common time patterns
  // Accepts: "9:00am-5:00pm", "9:00 AM - 5:00 PM", "14:00-17:00", "All day", etc.
  const hasTimePattern = /\d+[:]\d+|all day|tbd|tba/i.test(trimmed);

  if (!hasTimePattern && trimmed.length > 3) {
    // Soft warning
    errors.push('Warning: Time format may not be standard. Consider format like "9:00am-5:00pm"');
  }

  return {
    isValid: errors.length === 0,
    errors,
    sanitized: sanitizeText(trimmed),
  };
};

/**
 * Validates location field
 */
export const validateLocation = (location) => {
  const errors = [];

  if (!location || location.trim() === '') {
    errors.push('Location is required');
    return { isValid: false, errors };
  }

  const trimmed = location.trim();

  // Check length
  if (trimmed.length > MAX_LENGTHS.LOCATION) {
    errors.push(`Location must be less than ${MAX_LENGTHS.LOCATION} characters`);
  }

  // Check for script injection
  if (/<script|javascript:/i.test(trimmed)) {
    errors.push('Location contains invalid characters');
  }

  // Minimum length check
  if (trimmed.length < 3) {
    errors.push('Location must be at least 3 characters');
  }

  return {
    isValid: errors.length === 0,
    errors,
    sanitized: sanitizeText(trimmed),
  };
};

/**
 * Validates translation object
 */
export const validateTranslations = (translations) => {
  const errors = [];

  if (!translations || typeof translations !== 'object') {
    errors.push('Translations must be provided');
    return { isValid: false, errors };
  }

  // Validate English translations (required)
  if (!translations.en) {
    errors.push('English translations are required');
    return { isValid: false, errors };
  }

  const enNameValidation = validateTextField(
    translations.en.name,
    'English Name',
    MAX_LENGTHS.NAME,
    true
  );
  const enSubHeaderValidation = validateTextField(
    translations.en.subHeader,
    'English Sub-Header',
    MAX_LENGTHS.SUBHEADER,
    true
  );
  const enDescValidation = validateTextField(
    translations.en.description,
    'English Description',
    MAX_LENGTHS.DESCRIPTION,
    true
  );

  if (!enNameValidation.isValid) {
    errors.push(...enNameValidation.errors);
  }
  if (!enSubHeaderValidation.isValid) {
    errors.push(...enSubHeaderValidation.errors);
  }
  if (!enDescValidation.isValid) {
    errors.push(...enDescValidation.errors);
  }

  // Validate Spanish translations (optional)
  if (translations.es) {
    if (translations.es.name) {
      const esNameValidation = validateTextField(
        translations.es.name,
        'Spanish Name',
        MAX_LENGTHS.NAME,
        false
      );
      if (!esNameValidation.isValid) {
        errors.push(...esNameValidation.errors);
      }
    }
    if (translations.es.subHeader) {
      const esSubHeaderValidation = validateTextField(
        translations.es.subHeader,
        'Spanish Sub-Header',
        MAX_LENGTHS.SUBHEADER,
        false
      );
      if (!esSubHeaderValidation.isValid) {
        errors.push(...esSubHeaderValidation.errors);
      }
    }
    if (translations.es.description) {
      const esDescValidation = validateTextField(
        translations.es.description,
        'Spanish Description',
        MAX_LENGTHS.DESCRIPTION,
        false
      );
      if (!esDescValidation.isValid) {
        errors.push(...esDescValidation.errors);
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Master validation function for entire event
 */
export const validateEvent = (eventData) => {
  const allErrors = [];
  const warnings = [];

  // Validate translations
  const translationsValidation = validateTranslations(eventData.translations);
  if (!translationsValidation.isValid) {
    allErrors.push(...translationsValidation.errors);
  }

  // Validate image
  const imageValidation = validateImageURL(eventData.image);
  if (!imageValidation.isValid) {
    allErrors.push(...imageValidation.errors);
  } else if (imageValidation.warnings && imageValidation.warnings.length > 0) {
    warnings.push(...imageValidation.warnings);
  }

  // Validate date range
  const dateRangeValidation = validateDateRange(eventData.startDate, eventData.endDate);
  if (!dateRangeValidation.isValid) {
    allErrors.push(...dateRangeValidation.errors);
  }

  // Validate time
  const timeValidation = validateTime(eventData.time);
  if (!timeValidation.isValid) {
    allErrors.push(...timeValidation.errors);
  }

  // Validate location
  const locationValidation = validateLocation(eventData.location);
  if (!locationValidation.isValid) {
    allErrors.push(...locationValidation.errors);
  }

  // Validate optional URLs
  if (eventData.signUpLink) {
    const signUpValidation = validateURL(eventData.signUpLink, 'Signup Link', false);
    if (!signUpValidation.isValid) {
      allErrors.push(...signUpValidation.errors);
    }
  }

  if (eventData.learnMoreLink) {
    const learnMoreValidation = validateURL(eventData.learnMoreLink, 'Learn More Link', false);
    if (!learnMoreValidation.isValid) {
      allErrors.push(...learnMoreValidation.errors);
    }
  }

  // Check if both links are identical (warning, not error)
  if (
    eventData.signUpLink &&
    eventData.learnMoreLink &&
    eventData.signUpLink.trim() === eventData.learnMoreLink.trim()
  ) {
    warnings.push('Signup Link and Learn More Link are identical');
  }

  return {
    isValid: allErrors.length === 0,
    errors: allErrors,
    warnings: warnings,
  };
};

/**
 * Debounce helper for real-time validation
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};
